import { create } from 'zustand';
import { http } from '../api/http';

export interface Season {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  status: 'upcoming' | 'active' | 'completed';
  pointsMultiplier: number;
}

export interface SeasonPoints {
  id: number;
  seasonId: number;
  wallet: string;
  points: number;
  matchesPlayed: number;
  matchesWon: number;
  totalWageredLamports: string;
  lastEarnedAt: string | null;
}

export interface SeasonPointsEntry extends SeasonPoints {
  rank: number;
}

export interface PointEvent {
  id: number;
  seasonId: number;
  wallet: string;
  matchId: string | null;
  eventType: string;
  basePoints: number;
  multipliers: Record<string, number>;
  finalPoints: number;
  wagerLamports: string;
  createdAt: string;
}

export interface PointsUpdate {
  winner: {
    wallet: string;
    basePoints: number;
    multipliers: Record<string, number>;
    totalMultiplier: number;
    finalPoints: number;
    seasonTotal: number;
  };
  loser: {
    wallet: string;
    basePoints: number;
    multipliers: Record<string, number>;
    totalMultiplier: number;
    finalPoints: number;
    seasonTotal: number;
  };
}

interface SeasonPointsState {
  currentSeason: Season | null;
  myPoints: SeasonPoints | null;
  myRank: number;
  leaderboard: SeasonPointsEntry[];
  pointHistory: PointEvent[];
  lifetimePoints: number;
  loading: boolean;
  error: string | null;
  lastPointsUpdate: PointsUpdate | null;

  fetchCurrentSeason: () => Promise<void>;
  fetchMyPoints: (wallet: string, seasonId: number) => Promise<void>;
  fetchLeaderboard: (seasonId: number, limit?: number) => Promise<void>;
  fetchPointHistory: (wallet: string, seasonId: number) => Promise<void>;
  fetchLifetimePoints: (wallet: string) => Promise<void>;
  handlePointsUpdate: (update: PointsUpdate) => void;
}

export const useSeasonPointsStore = create<SeasonPointsState>((set, get) => ({
  currentSeason: null,
  myPoints: null,
  myRank: 0,
  leaderboard: [],
  pointHistory: [],
  lifetimePoints: 0,
  loading: false,
  error: null,
  lastPointsUpdate: null,

  fetchCurrentSeason: async () => {
    try {
      const res = await http.get<{ season: Season }>('/seasons/current');
      set({ currentSeason: res.season });
    } catch (error) {
      console.error('Failed to fetch current season:', error);
    }
  },

  fetchMyPoints: async (wallet: string, seasonId: number) => {
    try {
      const res = await http.get<{ points: SeasonPoints; rank: number }>(
        `/seasons/${seasonId}/points/${wallet}`,
      );
      set({ myPoints: res.points, myRank: res.rank });
    } catch (error) {
      console.error('Failed to fetch points:', error);
    }
  },

  fetchLeaderboard: async (seasonId: number, limit = 100) => {
    set({ loading: true });
    try {
      const res = await http.get<{ leaderboard: SeasonPointsEntry[] }>(
        `/seasons/${seasonId}/leaderboard?limit=${limit}`,
      );
      set({ leaderboard: res.leaderboard, loading: false });
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
      set({ loading: false });
    }
  },

  fetchPointHistory: async (wallet: string, seasonId: number) => {
    try {
      const res = await http.get<{ history: PointEvent[] }>(
        `/players/${wallet}/point-history?seasonId=${seasonId}`,
      );
      set({ pointHistory: res.history });
    } catch (error) {
      console.error('Failed to fetch point history:', error);
    }
  },

  fetchLifetimePoints: async (wallet: string) => {
    try {
      const res = await http.get<{ lifetime: number }>(`/players/${wallet}/lifetime-points`);
      set({ lifetimePoints: res.lifetime });
    } catch (error) {
      console.error('Failed to fetch lifetime points:', error);
    }
  },

  handlePointsUpdate: (update: PointsUpdate) => {
    set({ lastPointsUpdate: update });

    // Update season total if it's for the current user
    const { myPoints } = get();
    if (myPoints) {
      if (myPoints.wallet === update.winner.wallet) {
        set({
          myPoints: {
            ...myPoints,
            points: update.winner.seasonTotal,
            matchesPlayed: myPoints.matchesPlayed + 1,
            matchesWon: myPoints.matchesWon + 1,
          },
        });
      } else if (myPoints.wallet === update.loser.wallet) {
        set({
          myPoints: {
            ...myPoints,
            points: update.loser.seasonTotal,
            matchesPlayed: myPoints.matchesPlayed + 1,
          },
        });
      }
    }
  },
}));
